
import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Edit2, Archive, AlertCircle, Trash2, ClipboardList, CheckCircle, XCircle, Percent, DollarSign, Ban, Handshake, Award, CalendarDays, Globe, Target, Store, X } from 'lucide-react';
import { Campaign, Aggregator, Brand, CampaignScope, User, CampaignItem, CampaignItemCreate } from '../../../types';
import { CampaignStatus, AdminView, UserRole } from '../../../enums';
import { apiClient } from '../../../services/apiClient';
import { DatePicker } from '../../DatePicker';

interface CampaignsViewProps {
  campaigns: Campaign[];
  onUpdateCampaigns: (campaigns: Campaign[]) => void;
  onAddNotification: (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string, targetView?: AdminView, targetId?: string) => void;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  aggregators: Aggregator[];
  currencyCode?: string;
  brands: Brand[];
  currentUser: User;
}

export const CampaignsView: React.FC<CampaignsViewProps> = ({ campaigns, onUpdateCampaigns, onAddNotification, showToast, aggregators, currencyCode, brands, currentUser }) => {
  const currency = currencyCode || 'QAR';
  const [isCreating, setIsCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [coFundedEdited, setCoFundedEdited] = useState(false);
  const [sterlingEdited, setSterlingEdited] = useState(false);
  const [campaignItems, setCampaignItems] = useState<CampaignItem[]>([]);
  const [campaignItemsLoading, setCampaignItemsLoading] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState<{ nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string; price: string }>({
    nameEn: '',
    nameAr: '',
    descriptionEn: '',
    descriptionAr: '',
    price: '',
  });
  const [itemsError, setItemsError] = useState<string | null>(null);
  const aggregatorOptions = (aggregators || []).filter((a) => a.active !== false).map((a) => a.name);
  const brandLookup = new Map(brands.map((b) => [b.id, b]));
  const isManager = currentUser.role === UserRole.MANAGER;
  const allowedBrandIds = isManager && currentUser.brandIds && currentUser.brandIds.length ? currentUser.brandIds : [];
  const scopedBrands = isManager && allowedBrandIds.length ? brands.filter((b) => allowedBrandIds.includes(b.id)) : brands;

  const resolveScope = (scope: CampaignScope | undefined, ids: string[]) => {
    if (scope === 'ALL' || scope === 'SINGLE' || scope === 'MULTI') return scope;
    if (ids.length === 0) return 'ALL';
    if (ids.length === 1) return 'SINGLE';
    return 'MULTI';
  };
  
  const [newCampaign, setNewCampaign] = useState<Partial<Campaign>>({
    name: '',
    discountType: 'Percentage',
    discountValue: 0,
    aggregators: [],
    startDate: '',
    endDate: '',
    status: CampaignStatus.PLANNED,
    description: '',
    adminWarning: '',
    fundingType: 'None',
    sterlingPercent: undefined,
    coFundedPercent: undefined,
    brandIds: isManager ? allowedBrandIds : [],
    scope: isManager ? resolveScope(undefined, allowedBrandIds) : 'ALL'
  });
  const fundingType = newCampaign.fundingType || 'None';
  useEffect(() => {
    if (isCreating && newCampaign.id) {
      loadCampaignItems(newCampaign.id);
    } else {
      setCampaignItems([]);
    }
  }, [isCreating, newCampaign.id]);

  const loadCampaignItems = async (campaignId: string) => {
    setCampaignItemsLoading(true);
    setItemsError(null);
    try {
      const res = await apiClient.campaignItems.list(campaignId);
      setCampaignItems((res as any).items || []);
    } catch (e: any) {
      console.warn('Failed to load campaign items', e);
      setItemsError('Unable to load campaign items.');
    } finally {
      setCampaignItemsLoading(false);
    }
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};
    if (!newCampaign.name) errors.name = 'Required';
    if (!newCampaign.startDate) errors.startDate = 'Required';
    if (!newCampaign.endDate) errors.endDate = 'Required';
    const discountValNum = Number(newCampaign.discountValue);
    if (!newCampaign.discountValue && newCampaign.discountValue !== 0) errors.discountValue = 'Required';
    if (isNaN(discountValNum) || discountValNum <= 0) errors.discountValue = 'Must be greater than 0';
    if (newCampaign.discountType === 'Percentage' && discountValNum > 100) errors.discountValue = 'Cannot exceed 100%';
    if (newCampaign.startDate && newCampaign.endDate) {
      const start = new Date(newCampaign.startDate);
      const end = new Date(newCampaign.endDate);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end.getTime() < start.getTime()) {
        errors.endDate = 'End date cannot be before start date';
      }
    }

    const fundingType = newCampaign.fundingType || 'None';
    const sterVal = newCampaign.sterlingPercent;
    const coVal = newCampaign.coFundedPercent;

    const isValidPercent = (val: unknown) => typeof val === 'number' && !isNaN(val) && val >= 0 && val <= 100;

    if (fundingType === 'Co-Funded') {
      if (!isValidPercent(sterVal)) errors.sterlingPercent = 'Sterling % required (0-100)';
      if (!isValidPercent(coVal)) errors.coFundedPercent = 'Co-Funded % required (0-100)';
      if (isValidPercent(sterVal) && isValidPercent(coVal)) {
        const total = (sterVal || 0) + (coVal || 0);
        if (Math.round(total) !== 100) {
          errors.fundingTotal = 'Sterling % + Co-Funded % must total 100%';
        }
      }
    } else if (fundingType === 'Fully Funded') {
      if (!isValidPercent(sterVal)) errors.sterlingPercent = 'Sterling % required (0-100)';
      else if (sterVal !== 100) errors.sterlingPercent = 'Fully funded campaigns should total 100%';
    }

    const rawBrandIds = Array.from(new Set(newCampaign.brandIds || []));
    const selectedBrandIds = isManager ? rawBrandIds.filter((id) => allowedBrandIds.includes(id)) : rawBrandIds;
    const baseScope = resolveScope(newCampaign.scope as CampaignScope | undefined, selectedBrandIds);
    const scope = isManager && baseScope === 'ALL'
      ? resolveScope(undefined, allowedBrandIds)
      : baseScope;
    const finalScope = isManager ? resolveScope(scope as CampaignScope, scope === 'ALL' ? allowedBrandIds : selectedBrandIds) : scope;
    const brandIdsForSave =
      finalScope === 'ALL'
        ? (isManager ? allowedBrandIds : [])
        : (scope === 'ALL' && isManager ? allowedBrandIds : selectedBrandIds);
    if (isManager && allowedBrandIds.length === 0) {
      errors.brandIds = 'No brands assigned to you. Contact an admin to assign brands.';
    }
    if (finalScope !== 'ALL' && brandIdsForSave.length === 0) {
      errors.brandIds = 'Select at least one brand or choose All brands.';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: Campaign = {
      id: newCampaign.id || crypto.randomUUID(),
      name: newCampaign.name,
      discountType: (newCampaign.discountType as Campaign['discountType']) || 'Percentage',
      discountValue: Number(newCampaign.discountValue) || 0,
      aggregators: newCampaign.aggregators || [],
      startDate: newCampaign.startDate || '',
      endDate: newCampaign.endDate || '',
      status: newCampaign.status || CampaignStatus.PLANNED,
      description: newCampaign.description || '',
      adminWarning: newCampaign.adminWarning || '',
      fundingType,
      sterlingPercent: fundingType === 'None' ? undefined : (newCampaign.sterlingPercent ?? (fundingType === 'Fully Funded' ? 100 : undefined)),
      coFundedPercent: fundingType === 'Co-Funded' ? (newCampaign.coFundedPercent ?? 0) : undefined,
      brandIds: brandIdsForSave,
      scope: finalScope || 'ALL'
    };

    try {
      const savedList = newCampaign.id
        ? await apiClient.campaigns.update(payload.id, payload)
        : await apiClient.campaigns.create(payload);

      const normalized = Array.isArray(savedList) ? savedList : [savedList as any];
      onUpdateCampaigns(normalized);
      const title = newCampaign.id ? 'Campaign Updated' : 'Campaign Created';
      const message = `"${newCampaign.name}" has been saved.`;
      onAddNotification('success', title, message);
      showToast('success', title, message);
      setFormErrors({});
      setIsCreating(false);
      resetForm();
    } catch (error) {
      onAddNotification('error', 'Campaign Save Failed', 'Unable to persist campaign. Please retry.');
      showToast('error', 'Campaign Save Failed', 'Unable to persist campaign. Please retry.');
    }
  };

  const resetForm = () => {
    setNewCampaign({ 
        name: '', 
        discountType: 'Percentage', 
        discountValue: 0, 
        aggregators: [], 
        startDate: '', 
        endDate: '', 
        status: CampaignStatus.PLANNED,
        description: '',
        adminWarning: '',
        fundingType: 'None',
        sterlingPercent: undefined,
        coFundedPercent: undefined,
        brandIds: isManager ? allowedBrandIds : [],
        scope: isManager ? resolveScope(undefined, allowedBrandIds) : 'ALL'
    });
    setFormErrors({});
    setCoFundedEdited(false);
    setSterlingEdited(false);
    setCampaignItems([]);
    setItemModalOpen(false);
    setItemForm({ nameEn: '', nameAr: '', descriptionEn: '', descriptionAr: '', price: '' });
    setItemsError(null);
  }

  const handleEdit = (campaign: Campaign) => {
    const normalizedBrands = isManager
      ? (campaign.brandIds || []).filter((id) => allowedBrandIds.includes(id))
      : campaign.brandIds || [];
    setNewCampaign({ 
      ...campaign, 
      fundingType: campaign.fundingType || 'None',
      discountValue: campaign.discountValue ?? 0,
      sterlingPercent: campaign.sterlingPercent ?? (campaign.fundingType === 'Fully Funded' ? 100 : undefined),
      coFundedPercent: campaign.coFundedPercent ?? undefined,
      brandIds: isManager && normalizedBrands.length === 0 ? allowedBrandIds : normalizedBrands,
      scope: campaign.scope || resolveScope(undefined, normalizedBrands.length ? normalizedBrands : allowedBrandIds)
    });
    setIsCreating(true);
    setFormErrors({});
    setCoFundedEdited(false);
    setSterlingEdited(false);
    setCampaignItems([]);
    setItemsError(null);
    if (campaign.id) {
      loadCampaignItems(campaign.id);
    }
  };

  const handleDelete = async (campaign: Campaign) => {
    const confirmed = window.confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const updated = await apiClient.campaigns.delete(campaign.id);
      const normalized = Array.isArray(updated) ? updated : campaigns.filter(c => c.id !== campaign.id);
      onUpdateCampaigns(normalized);
      onAddNotification('warning', 'Campaign Deleted', `"${campaign.name}" has been removed.`, AdminView.CAMPAIGNS, campaign.id);
      showToast('warning', 'Campaign Deleted', `"${campaign.name}" has been removed.`);
    } catch (error: any) {
      // Extract error message from API response if available
      let errorMessage = 'Unable to delete campaign. Please retry.';
      if (error.message && error.message.includes('Cannot delete campaign')) {
         // Try to parse the detail from the error text if it's in the format "API DELETE ... failed: 400 ... - { ... }"
         // But apiClient throws "API DELETE ... failed: 400 ... - { "error": "...", "detail": "..." }"
         // We can try to extract the JSON part or just use the text.
         const match = error.message.match(/"detail":"([^"]+)"/);
         if (match && match[1]) {
             errorMessage = match[1];
         } else if (error.message.includes('linked submission')) {
             errorMessage = 'Cannot delete campaign because it has linked submissions.';
         }
      }
      
      onAddNotification('error', 'Delete Failed', errorMessage, AdminView.CAMPAIGNS, campaign.id);
      showToast('error', 'Delete Failed', errorMessage);
    }
  };

  const handleCreateNew = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleAddManualItem = async () => {
    if (!newCampaign.id) {
      setItemsError('Save the campaign first, then add items.');
      return;
    }
    const name = itemForm.nameEn.trim();
    const priceNum = Number(itemForm.price);
    if (!name) {
      setItemsError('Name is required.');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setItemsError('Price must be a valid number.');
      return;
    }
    setItemsError(null);
    try {
      const payload: CampaignItemCreate = {
        source: 'MANUAL',
        name: { en: name, ...(itemForm.nameAr.trim() ? { ar: itemForm.nameAr.trim() } : {}) },
        description: { en: itemForm.descriptionEn.trim(), ...(itemForm.descriptionAr.trim() ? { ar: itemForm.descriptionAr.trim() } : {}) },
        price: priceNum,
        image: null,
        choiceGroups: [],
        snapshotJson: {
          name: { en: name, ...(itemForm.nameAr.trim() ? { ar: itemForm.nameAr.trim() } : {}) },
          description: { en: itemForm.descriptionEn.trim(), ...(itemForm.descriptionAr.trim() ? { ar: itemForm.descriptionAr.trim() } : {}) },
          price: priceNum,
          image: null,
          choiceGroups: [],
        },
      };
      const res = await apiClient.campaignItems.add(newCampaign.id, [payload]);
      setCampaignItems((res as any).items || []);
      setItemModalOpen(false);
      setItemForm({ nameEn: '', nameAr: '', descriptionEn: '', descriptionAr: '', price: '' });
    } catch (e: any) {
      console.warn('Failed to add campaign item', e);
      setItemsError('Failed to add item.');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!newCampaign.id) return;
    try {
      const res = await apiClient.campaignItems.delete(newCampaign.id, itemId);
      setCampaignItems((res as any).items || []);
    } catch (e: any) {
      console.warn('Failed to delete campaign item', e);
      setItemsError('Failed to delete item.');
    }
  };

  const manualItemModal = itemModalOpen ? (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative">
        <button
          onClick={() => setItemModalOpen(false)}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
        >
          <X className="w-4 h-4" />
        </button>
        <h4 className="text-lg font-bold text-zinc-900 mb-4">Add Manual Item</h4>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-zinc-600 uppercase">Name (EN)</label>
            <input
              value={itemForm.nameEn}
              onChange={(e) => setItemForm({ ...itemForm, nameEn: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
              placeholder="Item name"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 uppercase">Name (AR)</label>
            <input
              value={itemForm.nameAr}
              onChange={(e) => setItemForm({ ...itemForm, nameAr: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
              placeholder="Arabic name (optional)"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 uppercase">Description (EN)</label>
            <textarea
              value={itemForm.descriptionEn}
              onChange={(e) => setItemForm({ ...itemForm, descriptionEn: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-teal-100 outline-none resize-none"
              rows={3}
              placeholder="Description (optional)"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 uppercase">Description (AR)</label>
            <textarea
              value={itemForm.descriptionAr}
              onChange={(e) => setItemForm({ ...itemForm, descriptionAr: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-teal-100 outline-none resize-none"
              rows={3}
              placeholder="Arabic description (optional)"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-600 uppercase">Price</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={itemForm.price}
              onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={() => setItemModalOpen(false)}
            className="px-4 py-2 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-sm hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAddManualItem}
            className="px-4 py-2 rounded-xl bg-teal-600 text-white font-bold text-sm hover:bg-teal-700"
          >
            Add Item
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const toggleAggregator = (agg: string) => {
     const current = newCampaign.aggregators || [];
     if (current.includes(agg)) {
        setNewCampaign({...newCampaign, aggregators: current.filter(a => a !== agg)});
     } else {
        setNewCampaign({...newCampaign, aggregators: [...current, agg]});
     }
  };

  const toggleBrand = (brandId: string) => {
    const current = newCampaign.brandIds || [];
    if (current.includes(brandId)) {
      setNewCampaign({ ...newCampaign, brandIds: current.filter((b) => b !== brandId) });
    } else {
      setNewCampaign({ ...newCampaign, brandIds: [...current, brandId] });
    }
    clearErrors('brandIds');
  };

  const clearErrors = (...keys: string[]) => {
    setFormErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  const isPercentDiscount = newCampaign.discountType === 'Percentage';

  const formatFundingShare = (campaign: Campaign) => {
    const isPercent = campaign.discountType === 'Percentage';
    const sterling = campaign.sterlingPercent ?? (campaign.fundingType === 'Fully Funded' ? 100 : undefined);
    const partner = campaign.coFundedPercent ?? (campaign.fundingType === 'Co-Funded' ? 0 : undefined);
    const dv = Number(campaign.discountValue) || 0;

    if (campaign.fundingType === 'Co-Funded') {
      if (isPercent) return `Sterling ${sterling ?? '-'}% / Partner ${partner ?? '-'}%`;
      const sAmt = sterling != null ? (dv * (sterling / 100)).toFixed(2) : '-';
      const pAmt = partner != null ? (dv * (partner / 100)).toFixed(2) : '-';
      return `Sterling ${currency} ${sAmt} / Partner ${currency} ${pAmt}`;
    }
    if (campaign.fundingType === 'Fully Funded') {
      if (isPercent) return `Sterling ${sterling ?? 100}%`;
      return `Sterling ${currency} ${dv.toFixed(2)}`;
    }
    return 'None';
  };

  const handleSterlingChange = (value: number | undefined) => {
    setSterlingEdited(true);
    if (value === undefined || isNaN(value)) {
      setNewCampaign({ ...newCampaign, sterlingPercent: undefined });
      return;
    }
    const nextVal = Math.max(0, Math.min(100, value));
    let updates: Partial<Campaign> = { sterlingPercent: nextVal };
    if (fundingType === 'Co-Funded' && !coFundedEdited) {
      const auto = Math.max(0, Math.min(100, 100 - nextVal));
      updates = { ...updates, coFundedPercent: auto };
    }
    setNewCampaign({ ...newCampaign, ...updates });
    clearErrors('sterlingPercent', 'fundingTotal');
  };

  const handleCoFundedChange = (value: number | undefined) => {
    setCoFundedEdited(true);
    if (value === undefined || isNaN(value)) {
      setNewCampaign({ ...newCampaign, coFundedPercent: undefined });
      return;
    }
    const nextVal = Math.max(0, Math.min(100, value));
    setNewCampaign({ ...newCampaign, coFundedPercent: nextVal });
    clearErrors('coFundedPercent', 'fundingTotal');
  };

  if (isCreating) {
      return (
        <>
          <div className="bg-white rounded-3xl border border-zinc-200 shadow-xl p-10 animate-fade-in-up max-w-7xl mx-auto">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-zinc-900 tracking-tight mb-2">
                  {newCampaign.id ? 'Edit Campaign' : 'Create New Campaign'}
                </h2>
                <p className="text-zinc-500">Configure campaign details, funding options, and participating aggregators</p>
              </div>
              
              {/* Campaign Basics */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">1</span>
                  Campaign Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-zinc-700">
                        Campaign Name <span className="text-red-500">*</span>
                      </label>
                      <input 
                          type="text" 
                          value={newCampaign.name}
                          onChange={(e) => { setNewCampaign({...newCampaign, name: e.target.value}); clearErrors('name'); }}
                          className={`w-full bg-white border-2 ${formErrors.name ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'} rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all`}
                          placeholder="e.g. Summer Special"
                      />
                      {formErrors.name && <span className="text-xs text-red-600 font-medium">{formErrors.name}</span>}
                    </div>
                      
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-zinc-700">Status</label>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setNewCampaign({...newCampaign, status: CampaignStatus.PLANNED})}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            newCampaign.status === CampaignStatus.PLANNED
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <ClipboardList className="w-4 h-4" />
                          Planned
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCampaign({...newCampaign, status: CampaignStatus.ACTIVE})}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            newCampaign.status === CampaignStatus.ACTIVE
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCampaign({...newCampaign, status: CampaignStatus.CLOSED})}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            newCampaign.status === CampaignStatus.CLOSED
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <XCircle className="w-4 h-4" />
                          Closed
                        </button>
                      </div>
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="block text-sm font-semibold text-zinc-700">Description</label>
                      <textarea 
                          value={newCampaign.description}
                          onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                          className="w-full bg-white border-2 border-zinc-200 hover:border-zinc-300 rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none h-28 resize-none transition-all"
                          placeholder="Brief description of the campaign..."
                      />
                    </div>
                </div>
              </div>

              {/* Pricing & Funding */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">2</span>
                  Pricing & Funding
                </h3>
                <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-zinc-700">Discount Type</label>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setNewCampaign({ ...newCampaign, discountType: 'Percentage' })}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            newCampaign.discountType === 'Percentage'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <Percent className="w-4 h-4" />
                          Percentage
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCampaign({ ...newCampaign, discountType: 'Flat' })}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            newCampaign.discountType === 'Flat'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <DollarSign className="w-4 h-4" />
                          Flat Amount
                        </button>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-zinc-700">
                          {newCampaign.discountType === 'Flat' ? 'Discount Value (Amount)' : 'Discount Value (%)'} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input 
                                type="number" 
                                value={newCampaign.discountValue ?? ''}
                                onChange={(e) => {
                                  setNewCampaign({...newCampaign, discountValue: e.target.value === '' ? undefined : Number(e.target.value)});
                                  clearErrors('discountValue');
                                }}
                                className={`w-full bg-white border-2 ${formErrors.discountValue ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'} rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all`}
                                placeholder={newCampaign.discountType === 'Flat' ? 'Enter amount' : 'Enter percent'}
                              />
                              <span className="absolute right-4 top-3.5 text-xs font-bold text-zinc-400 uppercase">
                                {newCampaign.discountType === 'Flat' ? currency : '%'}
                              </span>
                        </div>
                        {formErrors.discountValue && <span className="text-xs text-red-600 font-medium">{formErrors.discountValue}</span>}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <label className="block text-sm font-semibold text-zinc-700">Funding Type</label>
                          <span className="text-[11px] font-bold text-zinc-400 uppercase bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-100">
                            Discount Type: {isPercentDiscount ? 'Percentage' : 'Flat'}
                          </span>
                        </div>
                        {formErrors.fundingTotal && (
                          <span className="text-xs font-semibold text-red-600">{formErrors.fundingTotal}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setNewCampaign({
                              ...newCampaign,
                              fundingType: 'None',
                              sterlingPercent: undefined,
                              coFundedPercent: undefined
                            });
                            clearErrors('fundingTotal', 'sterlingPercent', 'coFundedPercent');
                            setCoFundedEdited(false);
                            setSterlingEdited(false);
                          }}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            fundingType === 'None'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <Ban className="w-4 h-4" />
                          None
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNewCampaign({
                              ...newCampaign,
                              fundingType: 'Co-Funded',
                              sterlingPercent: newCampaign.sterlingPercent ?? 50,
                              coFundedPercent: newCampaign.coFundedPercent ?? 50
                            });
                            clearErrors('fundingTotal', 'sterlingPercent', 'coFundedPercent');
                            setCoFundedEdited(false);
                            setSterlingEdited(false);
                          }}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            fundingType === 'Co-Funded'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <Handshake className="w-4 h-4" />
                          Co-Funded
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNewCampaign({
                              ...newCampaign,
                              fundingType: 'Fully Funded',
                              sterlingPercent: 100,
                              coFundedPercent: undefined
                            });
                            clearErrors('fundingTotal', 'sterlingPercent', 'coFundedPercent');
                            setCoFundedEdited(false);
                            setSterlingEdited(false);
                          }}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${
                            fundingType === 'Fully Funded'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          <Award className="w-4 h-4" />
                          Fully Funded
                        </button>
                      </div>
                    </div>

                      {fundingType === 'Co-Funded' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 animate-fade-in-up">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-zinc-700">
                              {isPercentDiscount ? 'Sterling %' : 'Sterling Amount'} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              value={newCampaign.sterlingPercent ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : Number(e.target.value);
                                handleSterlingChange(val);
                              }}
                              className={`w-full bg-white border-2 ${formErrors.sterlingPercent ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'} rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all`}
                              min={0}
                              max={100}
                              placeholder="50"
                            />
                            {formErrors.sterlingPercent && <span className="text-xs text-red-600 font-medium">{formErrors.sterlingPercent}</span>}
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-zinc-700">
                              {isPercentDiscount ? 'Co-Funded %' : 'Co-Funded Amount'} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              value={newCampaign.coFundedPercent ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : Number(e.target.value);
                                handleCoFundedChange(val);
                              }}
                              className={`w-full bg-white border-2 ${formErrors.coFundedPercent ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'} rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all`}
                              min={0}
                              max={100}
                              placeholder="50"
                            />
                            {formErrors.coFundedPercent && <span className="text-xs text-red-600 font-medium">{formErrors.coFundedPercent}</span>}
                          </div>
                      </div>
                      )}

                    {fundingType === 'Fully Funded' && (
                      <div className="pt-4 animate-fade-in-up">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-zinc-700">Sterling % <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            value={newCampaign.sterlingPercent ?? ''}
                            onChange={(e) => {
                              const val = e.target.value === '' ? undefined : Number(e.target.value);
                              setNewCampaign({ ...newCampaign, sterlingPercent: val });
                              clearErrors('sterlingPercent', 'fundingTotal');
                            }}
                            className={`w-full bg-white border-2 ${formErrors.sterlingPercent ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'} rounded-xl px-4 py-3.5 font-medium focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all`}
                            min={0}
                            max={100}
                            placeholder="100"
                          />
                          {formErrors.sterlingPercent && <span className="text-xs text-red-600 font-medium">{formErrors.sterlingPercent}</span>}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Warning Message */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">3</span>
                  Important Requirements
                </h3>
                <div className="relative">
                    <AlertCircle className="absolute left-4 top-4 w-5 h-5 text-amber-600" />
                    <input 
                       type="text" 
                       value={newCampaign.adminWarning}
                       onChange={(e) => setNewCampaign({...newCampaign, adminWarning: e.target.value})}
                       className="w-full bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 hover:border-amber-300 rounded-xl pl-12 pr-4 py-3.5 font-medium text-amber-900 placeholder:text-amber-500/60 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none transition-all"
                       placeholder="e.g. Ensure item names match aggregator menus exactly..."
                    />
                </div>
              </div>

              {/* Brand Targeting */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">4</span>
                  Brand Targeting
                </h3>
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => { setNewCampaign({ ...newCampaign, scope: 'ALL', brandIds: [] }); clearErrors('brandIds'); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${newCampaign.scope === 'ALL' || !newCampaign.scope ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:text-teal-700'}`}
                  >
                    <Globe className="w-4 h-4" /> All Brands
                  </button>
                  <button
                    onClick={() => { setNewCampaign({ ...newCampaign, scope: resolveScope('MULTI', newCampaign.brandIds || []) }); clearErrors('brandIds'); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${(newCampaign.scope && newCampaign.scope !== 'ALL') ? 'bg-zinc-900 border-zinc-900 text-white shadow-lg shadow-zinc-900/10' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:text-teal-700'}`}
                  >
                    <Target className="w-4 h-4" /> Specific Brands
                  </button>
                  <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">
                    {newCampaign.scope === 'ALL' || !newCampaign.scope ? 'Global' : 'Scoped'}
                  </span>
                </div>

                {formErrors.brandIds && <div className="text-xs text-red-600 font-semibold mb-2">{formErrors.brandIds}</div>}

                {scopedBrands.length === 0 ? (
                  <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-500">
                    No brands available yet. Create brands first to target them.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {scopedBrands.map((brand) => {
                      const selected = (newCampaign.brandIds || []).includes(brand.id);
                      const isAll = newCampaign.scope === 'ALL' || !newCampaign.scope;
                      return (
                        <button
                          key={brand.id}
                          onClick={() => !isAll && toggleBrand(brand.id)}
                          className={`flex items-center justify-between gap-2 p-4 rounded-2xl border-2 transition-all text-left ${selected ? 'bg-teal-50 border-teal-200 text-teal-800 shadow-sm shadow-teal-200' : 'bg-white border-zinc-200 text-zinc-700 hover:border-teal-200 hover:text-teal-700'} ${isAll ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-zinc-50 border border-zinc-100 flex items-center justify-center overflow-hidden">
                              {brand.logo ? <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" /> : <Store className="w-5 h-5 text-zinc-300" />}
                            </div>
                            <div>
                              <div className="font-bold text-sm">{brand.name}</div>
                              <div className="text-[11px] text-zinc-500">ID: {brand.id}</div>
                            </div>
                          </div>
                          {!isAll && (
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? 'bg-teal-600 border-teal-600 text-white' : 'border-zinc-300 text-transparent'}`}>
                              <CheckCircle className="w-3 h-3" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 mt-3">If no brands are selected, the campaign is treated as global.</p>
              </div>

              {/* Aggregators */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">5</span>
                  Participating Aggregators
                </h3>
                <div className="flex flex-wrap gap-3">
                    {aggregatorOptions.map(agg => (
                        <button 
                          key={agg}
                          onClick={() => toggleAggregator(agg)}
                          className={`px-5 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 ${newCampaign.aggregators?.includes(agg) ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-600/20 scale-105' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700'}`}
                        >
                          {agg}
                        </button>
                    ))}
                </div>
              </div>

              {/* Date Range */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">6</span>
                  Campaign Period
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <DatePicker
                      selected={newCampaign.startDate ? new Date(newCampaign.startDate) : null}
                      onChange={(date) => {
                        setNewCampaign({...newCampaign, startDate: date ? date.toISOString().split('T')[0] : ''});
                        clearErrors('startDate', 'endDate');
                      }}
                      placeholderText="Select start date"
                      label="Valid From"
                      className={formErrors.startDate ? 'border-red-300 ring-2 ring-red-100' : ''}
                    />
                    {formErrors.startDate && <span className="text-xs text-red-500 font-semibold">{formErrors.startDate}</span>}
                  </div>
                  <div className="space-y-1">
                    <DatePicker
                      selected={newCampaign.endDate ? new Date(newCampaign.endDate) : null}
                      onChange={(date) => {
                        setNewCampaign({...newCampaign, endDate: date ? date.toISOString().split('T')[0] : ''});
                        clearErrors('endDate');
                      }}
                      placeholderText="Select end date"
                      label="Valid To"
                      minDate={newCampaign.startDate ? new Date(newCampaign.startDate) : undefined}
                      className={formErrors.endDate ? 'border-red-300 ring-2 ring-red-100' : ''}
                    />
                    {formErrors.endDate && <span className="text-xs text-red-500 font-semibold">{formErrors.endDate}</span>}
                  </div>
                </div>
              </div>

              {/* Campaign Items */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs">7</span>
                  Campaign Items
                </h3>
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm text-zinc-600">Add items for this campaign. Manual now; Menu import coming next.</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setItemModalOpen(true)}
                      disabled={!newCampaign.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold border border-zinc-200 bg-white hover:border-teal-200 hover:text-teal-700 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" /> Add Manual Item
                    </button>
                    <button
                      type="button"
                      disabled
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold border border-dashed border-zinc-300 text-zinc-400 bg-white"
                      title="Coming next"
                    >
                      <Plus className="w-4 h-4" /> Add from Menu (soon)
                    </button>
                  </div>
                </div>
                {itemsError && <div className="text-sm text-red-600 mb-2">{itemsError}</div>}
                {!newCampaign.id ? (
                  <div className="p-4 rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 bg-zinc-50">
                    Save the campaign first, then add items.
                  </div>
                ) : campaignItemsLoading ? (
                  <div className="p-4 rounded-lg border border-zinc-200 bg-white text-sm text-zinc-500">Loading items...</div>
                ) : campaignItems.length === 0 ? (
                  <div className="p-4 rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 bg-zinc-50">
                    No items yet. Add a manual item.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaignItems.map((item) => (
                      <div key={item.id} className="p-4 rounded-lg border border-zinc-200 bg-white shadow-sm flex items-start gap-3">
                        <div className="w-12 h-12 rounded-md bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
                          {item.image ? <img src={item.image} alt={item.name.en} className="w-full h-full object-cover" /> : <ClipboardList className="w-5 h-5 text-zinc-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-zinc-900">{item.name.en}</span>
                            {item.name.ar && <span className="text-xs font-semibold text-zinc-500">/ {item.name.ar}</span>}
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200">
                              {item.source === 'MENU_BUILDER' ? 'MENU' : 'MANUAL'}
                            </span>
                          </div>
                          {item.description?.en && <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{item.description.en}</div>}
                          <div className="text-sm font-bold text-teal-700 mt-2">{currency} {item.price.toFixed(2)}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-8 border-t-2 border-zinc-100">
                  <button 
                    onClick={() => { setIsCreating(false); resetForm(); }} 
                    className="px-8 py-3.5 text-zinc-600 font-bold hover:bg-zinc-100 rounded-xl transition-all duration-200 border-2 border-zinc-200 hover:border-zinc-300"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSave} 
                    className="px-8 py-3.5 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-bold rounded-xl shadow-lg shadow-teal-600/25 hover:shadow-xl hover:shadow-teal-600/30 transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    {newCampaign.id ? '✓ Update Campaign' : '✓ Save Campaign'}
                  </button>
              </div>
          </div>
          {manualItemModal}
        </>
      )
  }

  return (
    <div className="animate-fade-in space-y-6">
       <div className="flex justify-between items-center">
        <div>
            <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Campaigns</h2>
            <p className="text-zinc-500 text-sm">Manage discount rules and active periods.</p>
            <p className="text-xs text-zinc-500">Loaded {campaigns.length} campaigns from API.</p>
        </div>
        <button onClick={handleCreateNew} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-xl font-bold shadow-md shadow-teal-200 transition-all">
            <Plus className="w-4 h-4" />
            Create New Campaign
        </button>
       </div>

       <div className="grid gap-4">
           {campaigns.map(campaign => (
               <div key={campaign.id} className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-zinc-900">{campaign.name}</h3>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${campaign.status === CampaignStatus.ACTIVE ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                                {campaign.status}
                            </span>
                        </div>
                        <p className="text-zinc-500 text-sm mb-4">{campaign.description}</p>
                        
                        {campaign.adminWarning && (
                            <div className="flex items-start gap-2 bg-amber-50 text-amber-800 text-xs font-semibold p-3 rounded-lg border border-amber-100 mb-4 max-w-2xl">
                                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                {campaign.adminWarning}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-4 text-sm">
                             <div className="flex items-center gap-1.5 font-bold text-zinc-700 bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-100">
                                <span className="text-teal-700">
                                  {campaign.discountValue}{campaign.discountType === 'Percentage' ? '%' : ` ${currency}`}
                                </span>
                                <span className="text-zinc-600">Discount</span>
                             </div>
                             <div className="flex items-center gap-1.5 font-medium text-zinc-500 bg-zinc-50 px-3 py-1.5 rounded-lg border border-zinc-100">
                                <span className="text-[10px] font-bold uppercase text-zinc-400">Type</span>
                                <span className="font-bold text-zinc-700">{campaign.discountType}</span>
                             </div>
                             <div className="flex items-center gap-1.5 font-medium text-zinc-500 bg-zinc-50 px-3 py-1.5 rounded-lg border border-zinc-100">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{campaign.startDate} - {campaign.endDate}</span>
                             </div>
                             <div className="flex items-center gap-2 font-medium text-zinc-600 bg-zinc-50 px-3 py-1.5 rounded-lg border border-zinc-100">
                                <span className="text-[10px] font-bold uppercase text-zinc-400">Funding</span>
                                <span className="font-bold text-zinc-800">{formatFundingShare(campaign)}</span>
                             </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {campaign.scope === 'ALL' || !campaign.brandIds || campaign.brandIds.length === 0 ? (
                            <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-1 rounded border border-emerald-100 font-bold uppercase">All Brands</span>
                          ) : (
                            campaign.brandIds.map((bid) => (
                              <span key={bid} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-1 rounded border border-zinc-200 font-bold uppercase">
                                {brandLookup.get(bid)?.name || bid}
                              </span>
                            ))
                          )}
                          <span className="text-[10px] bg-zinc-50 text-zinc-500 px-2 py-1 rounded border border-zinc-200 font-bold uppercase">
                            Scope: {campaign.scope || (campaign.brandIds?.length ? (campaign.brandIds.length > 1 ? 'MULTI' : 'SINGLE') : 'ALL')}
                          </span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2">
                          {campaign.aggregators.map(agg => (
                              <span key={agg} className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-1 rounded border border-zinc-200 font-bold uppercase">{agg}</span>
                          ))}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button 
                            onClick={() => handleEdit(campaign)}
                            className="p-2 text-zinc-400 hover:bg-zinc-50 hover:text-teal-600 rounded-lg transition-colors"
                        >
                            <Edit2 className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => handleDelete(campaign)}
                            className="p-2 text-zinc-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors"
                            title="Delete campaign"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
               </div>
           ))}
       </div>
    </div>
  );
};
